import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Space, Table } from 'antd'
import Checkbox from 'antd/es/checkbox'
import { ColumnProps } from 'antd/lib/table'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useColumnSearchProps } from '../../../../../components/Table/useColumSearchProps'
import { parseDate } from '../../../../../config/dayjsConfig'
import { setReallocationsPageRowKeys } from '../../../../../redux/pages/settings/reallocations/reallocations/actions'
import { reallocationsPageSelector } from '../../../../../redux/pages/settings/reallocations/reallocations/selectors'
import { ReallocationSource, ReallocationType } from '../../../../../types/reallocation/Reallocation'
import { ReallocationContext, ReallocationContextType } from './ReallocationProvider'
import ReallocationTargetTable from './ReallocationTargetTable'
import { AppDispatch } from '../../../../../redux/store'
import { useCtxDimensions } from '../../../../../features/dimension/queries/useCtxDimensions'
import { useCtxFormulas } from '../../../../../features/formulas/queries/useCtxFormulas'
import { useFetchReallocations } from '../../../../../features/reallocation'
import { useDeleteConfirmModal } from '../../../../../shared/modals/deleteConfirmModal'

function ReallocationTable() {
  const dispatch: AppDispatch = useDispatch()

  const { data: reallocations = [], isLoading: reallocationsLoading } = useFetchReallocations()
  const { expandedRowKeys } = useSelector(reallocationsPageSelector)
  const { data: dimensions = [] } = useCtxDimensions()
  const { data: formulas = [] } = useCtxFormulas()
  const { handleNewSource, handleEditSource, handleDeleteSource } = useContext(
    ReallocationContext
  ) as ReallocationContextType
  const { t } = useTranslation()
  const deleteConfirmModal = useDeleteConfirmModal()

  const sourceDeleteLabel = (record: ReallocationSource) => {
    const dimensionName = record.dimensionId && dimensions?.find(d => d.dimensionId === record.dimensionId)?.name
    return [record.description, dimensionName].filter(Boolean).join(' - ')
  }

  const typeRenderer = ({ type, ...source }: ReallocationSource) => {
    switch (type) {
      case ReallocationType.fixed:
      case ReallocationType.floating:
        return t(`reallocationsPage:${type}`, '')
      case ReallocationType.keyFigureId:
        return t(`keyFigureStatement:${source[type]}`, '')
      case ReallocationType.formulaId:
        return formulas.find(f => f.id === source[type])?.name || ''
      default:
        return ''
    }
  }

  const columns: ColumnProps<ReallocationSource>[] = [
    {
      key: 'dimensionId',
      title: t('global:dimension'),
      dataIndex: 'dimensionId',
      ...useColumnSearchProps('dimensionId', val => {
        return (dimensions && dimensions?.find(d => d.dimensionId === val)?.name) || ''
      })
    },
    {
      key: 'description',
      title: t('global:description'),
      dataIndex: 'description',
      ...useColumnSearchProps('description')
    },
    {
      key: 'actuals',
      title: t('global:actuals'),
      dataIndex: 'actuals',
      render: (val: boolean) => <Checkbox disabled checked={val} />
    },
    {
      key: 'budget',
      title: t('global:budget'),
      dataIndex: 'budget',
      render: (val: boolean) => <Checkbox disabled checked={val} />
    },
    {
      key: 'period',
      title: t('global:time-period'),
      dataIndex: 'period',
      render: (value, record) =>
        record.startDate && record.endDate
          ? `${parseDate(record.startDate)?.format('MM/YYYY')} - ${parseDate(record.endDate)?.format('MM/YYYY')}`
          : t('global:indefinitely')
    },
    {
      key: 'type',
      title: t('global:type'),
      dataIndex: 'type',
      ...useColumnSearchProps('type', (val, record: ReallocationSource) => typeRenderer(record))
    },
    {
      key: 'actions',
      title: t('global:actions'),
      align: 'right',
      dataIndex: 'actions',
      render: (val: string, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              handleEditSource(record)
            }}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() =>
              deleteConfirmModal({
                content: sourceDeleteLabel(record),
                onOk: () => handleDeleteSource(record)
              })
            }
          />
        </Space>
      )
    }
  ]

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleNewSource()}>
          {t('global:add-new')}
        </Button>
      </div>
      <Table
        loading={reallocationsLoading}
        size="small"
        rowKey="id"
        bordered={false}
        dataSource={reallocations}
        columns={columns}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: rows => dispatch(setReallocationsPageRowKeys(rows as number[])),
          expandedRowRender: record => <ReallocationTargetTable reallocation={record} targets={record.targets} />
        }}
      />
    </>
  )
}

export default ReallocationTable
